import { SelectOption } from 'src/app/utils/formatSelectOptions';

import {
  AcquisitionType,
  MarketplaceOfferSelectData,
  MarketplaceOfferV2,
  MarketplacePropertiesResponse,
} from '../types/types';

function getFirstString(...values: unknown[]): string {
  const stringValue = values.find(
    value => typeof value === 'string' && value.trim().length > 0
  ) as string | undefined;

  return stringValue?.trim() ?? '';
}

function getFirstNumber(...values: unknown[]): number {
  const numeric = values.find(
    value => typeof value === 'number' && Number.isFinite(value)
  ) as number | undefined;

  return numeric ?? 0;
}

const TAG_TRANSLATION_MAP: Record<string, string> = {
  promotional: 'Promocional',
};

function translateTag(tag: string | undefined): string | undefined {
  if (!tag) return undefined;
  const normalized = tag.trim().toLowerCase().replace(/\s+/g, '_');
  return TAG_TRANSLATION_MAP[normalized] ?? tag;
}

function resolveAcquisitionType(
  acquisitionId: number | undefined,
  properties?: MarketplacePropertiesResponse
): AcquisitionType {
  if (acquisitionId !== undefined && properties?.acquisitionTypes) {
    const code = properties.acquisitionTypes.find(
      a => a.id === acquisitionId
    )?.code;
    if (code && (Object.values(AcquisitionType) as string[]).includes(code)) {
      return code as AcquisitionType;
    }
  }
  return AcquisitionType.Purchase;
}

export function mapOfferToSelectData(
  offer: MarketplaceOfferV2,
  properties?: MarketplacePropertiesResponse
): MarketplaceOfferSelectData {
  const id = getFirstNumber(offer.id);
  const offerId = getFirstString(offer.offerId, offer.id?.toString());
  const checkoutId = getFirstString(offer.checkoutId);
  const name = getFirstString(offer.name, `Oferta ${offerId || id}`);
  const fullPrice = getFirstNumber(
    offer.fullPrice,
    offer.listPrice,
    offer.salePrice
  );
  const listPrice = getFirstNumber(
    offer.listPrice,
    offer.fullPrice,
    offer.salePrice
  );
  const salePrice = getFirstNumber(
    offer.salePrice,
    offer.fullPrice,
    offer.listPrice
  );
  const tag = translateTag(getFirstString(offer.tag) || undefined);

  const typeName = properties?.types?.find(t => t.id === offer.typeId)?.name;
  const seasonalityName = properties?.seasonalities?.find(
    s => s.id === offer.seasonalityId
  )?.name;
  const acquisitionName = properties?.acquisitionTypes?.find(
    a => a.id === offer.acquisitionId
  )?.name;

  return {
    id,
    offerId,
    checkoutId,
    name,
    fullPrice,
    listPrice,
    salePrice,
    type: resolveAcquisitionType(offer.acquisitionId, properties),
    typeName,
    seasonalityName,
    acquisitionName,
    tag,
  };
}

export function augmentOfferSelectOptions(
  options: SelectOption<{ offer: MarketplaceOfferSelectData }>[],
  payOffer: MarketplaceOfferV2 | undefined,
  properties: MarketplacePropertiesResponse | undefined
): SelectOption<{ offer: MarketplaceOfferSelectData }>[] {
  if (!payOffer) return options;
  const payOfferId = String(payOffer.offerId ?? payOffer.id ?? '');
  if (options.some(o => o.value === payOfferId)) return options;
  const syntheticData = mapOfferToSelectData(payOffer, properties);
  return [
    { value: payOfferId, label: payOfferId, offer: syntheticData },
    ...options,
  ];
}
